
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import type { Application } from '@/types/database.types';
import { useEffect, useState } from 'react';

interface MedicalCarrierInformationProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const MedicalCarrierInformation = ({
  register,
  watch,
  setValue,
  data
}: MedicalCarrierInformationProps) => {
  const [carriersDisplayValue, setCarriersDisplayValue] = useState('');

  useEffect(() => {
    // Initialize values on component mount only
    if (data) {
      // Handle current_medical_carrier initialization
      setValue('current_medical_carrier', data.current_medical_carrier === null ? '' : data.current_medical_carrier || '');
      
      if (data.next_medical_renewal) {
        setValue('next_medical_renewal', data.next_medical_renewal);
      }
      
      // For arrays like desired carriers - ensure it's initialized as empty array not null
      if (!data.desired_medical_carriers) {
        setValue('desired_medical_carriers', []);
        setCarriersDisplayValue('');
      } else if (typeof data.desired_medical_carriers === 'string') {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(data.desired_medical_carriers as string);
          const carriers = Array.isArray(parsed) ? parsed : [data.desired_medical_carriers];
          setValue('desired_medical_carriers', carriers);
          setCarriersDisplayValue(carriers.join(', '));
        } catch (e) {
          // If parsing fails, treat it as a single value
          setValue('desired_medical_carriers', [data.desired_medical_carriers]);
          setCarriersDisplayValue(data.desired_medical_carriers as string);
        }
      } else if (Array.isArray(data.desired_medical_carriers)) {
        setValue('desired_medical_carriers', data.desired_medical_carriers);
        setCarriersDisplayValue(data.desired_medical_carriers.join(', '));
      }
      
      // Ensure boolean fields have proper boolean values
      setValue('is_self_funded', Boolean(data.is_self_funded));
      setValue('has_high_cost_claims', Boolean(data.has_high_cost_claims));
      setValue('medical_with_peo', Boolean(data.medical_with_peo));
      setValue('ancillary_with_peo', Boolean(data.ancillary_with_peo));
    }
  }, [data, setValue]);

  const handleDesiredCarrierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCarriersDisplayValue(value);
  };

  const handleDesiredCarrierBlur = () => {
    // Convert to array when user finishes typing
    const carriers = carriersDisplayValue.split(',').map(c => c.trim()).filter(Boolean);
    setValue('desired_medical_carriers', carriers);
    console.log('Medical carriers updated:', carriers);
  };

  // Helper function to handle date conversion
  const handleDateSelect = (date: Date | undefined, fieldName: string) => {
    if (date) {
      // Format as ISO string for database storage
      setValue(fieldName as any, date.toISOString());
    } else {
      setValue(fieldName as any, undefined);
    }
  };

  // Parse the date from string to Date object for the DatePicker
  const parseDate = (dateString: string | null | undefined): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  // Get the current values from the form
  const nextMedicalRenewal = watch('next_medical_renewal');
  const parsedDate = parseDate(nextMedicalRenewal);
  const isSelfFunded = watch('is_self_funded');
  const hasHighCostClaims = watch('has_high_cost_claims');
  const medicalWithPeo = watch('medical_with_peo');

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="current_medical_carrier">Current Medical Carrier</Label>
        <Input
          id="current_medical_carrier"
          placeholder="Enter current medical insurance carrier"
          {...register('current_medical_carrier')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="desired_medical_carriers">Desired Medical Carriers (comma separated)</Label>
        <Input
          id="desired_medical_carriers"
          value={carriersDisplayValue}
          onChange={handleDesiredCarrierChange}
          onBlur={handleDesiredCarrierBlur}
          placeholder="Blue Cross, UnitedHealthcare, etc."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="next_medical_renewal">Next Medical Renewal Date</Label>
        <DatePicker
          id="next_medical_renewal"
          date={parsedDate}
          onSelect={(date) => handleDateSelect(date, 'next_medical_renewal')}
          placeholder="Select renewal date"
        />
      </div>

      <div className="flex items-center justify-between mt-4">
        <Label htmlFor="is_self_funded" className="cursor-pointer">
          Does the organization currently use Level-Funding or Self-Funding for Medical Benefits?
        </Label>
        <Switch
          id="is_self_funded"
          checked={Boolean(isSelfFunded)}
          onCheckedChange={(checked) => {
            setValue('is_self_funded', Boolean(checked));
            // Clear medical_with_peo when self-funded is enabled since they're mutually exclusive
            if (checked) {
              setValue('medical_with_peo', false);
            }
          }}
        />
      </div>

      {/* Show level/self-funded details when self-funded is true */}
      {isSelfFunded && (
        <div className="space-y-2 ml-6">
          <Label htmlFor="self_funded_details">Please specify and provide details:</Label>
          <Textarea
            id="self_funded_details"
            {...register('self_funded_details')}
            placeholder="Please specify Level-Funded or Self-Funded and provide any relevant details about the arrangement"
            rows={3}
          />
        </div>
      )}

      {/* Only show PEO question when NOT self-funded */}
      {!isSelfFunded && (
        <div className="flex items-center justify-between mt-4">
          <Label htmlFor="medical_with_peo" className="cursor-pointer">
            Are Medical Benefits currently through a PEO?
          </Label>
          <Switch
            id="medical_with_peo"
            checked={Boolean(medicalWithPeo)}
            onCheckedChange={(checked) => setValue('medical_with_peo', Boolean(checked))}
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label htmlFor="has_high_cost_claims" className="cursor-pointer">
          Have there been any claims exceeding $25,000 in the past 24 months?
        </Label>
        <Switch
          id="has_high_cost_claims"
          checked={Boolean(hasHighCostClaims)}
          onCheckedChange={(checked) => {
            setValue('has_high_cost_claims', !!checked);
            if (!checked) {
              setValue('high_cost_claims_details', '');
            }
          }}
        />
      </div>
      
      {hasHighCostClaims && (
        <div className="space-y-2 ml-6">
          <Label htmlFor="high_cost_claims_details">Provide details on high cost claims</Label>
          <Textarea
            id="high_cost_claims_details"
            {...register('high_cost_claims_details')}
            placeholder="Please provide details about nature of claims, estimated costs, and current status"
            rows={4}
          />
        </div>
      )}
    </div>
  );
};
